import { Pipe, PipeTransform } from '@angular/core';

/**
 * Reason an ATO order landed in `needs_review`.
 *
 * Mirrors the `ReviewReason` enum in volta-ai-shared
 * (`src/volta_ai_shared/visibility.py`). `VisibilityReporter.review()` sends
 * `process_error=reason.value`, so `process_error` carries the *code* verbatim
 * (e.g. `"PARSE_EMPTY"`) and `operator_notes` carries the AI's free-text English
 * detail. The code is a stable machine token — it must never reach the user
 * as-is (WB-7431).
 */
export type ReviewReasonCode =
  | 'REJECTED_RECIPIENT'
  | 'REJECTED_SENDER'
  | 'REJECTED_SPAM'
  | 'INVALID_EMAIL'
  | 'PARSE_FAILED'
  | 'PARSE_EMPTY'
  | 'MATCH_NONE'
  | 'MATCH_LOW_CONFIDENCE'
  | 'CALLBACK_FAILED'
  | 'INTERNAL_ERROR';

export const REVIEW_REASON_CODES: readonly ReviewReasonCode[] = [
  'REJECTED_RECIPIENT',
  'REJECTED_SENDER',
  'REJECTED_SPAM',
  'INVALID_EMAIL',
  'PARSE_FAILED',
  'PARSE_EMPTY',
  'MATCH_NONE',
  'MATCH_LOW_CONFIDENCE',
  'CALLBACK_FAILED',
  'INTERNAL_ERROR',
];

/**
 * The review-outcome fields of an ATO row. Both `IQuotation` and `IOrder`
 * satisfy this structurally, so the pipe takes the row itself.
 */
export interface IReviewReasonSource {
  /** Forward-compatible: used first if the platform ever persists the code explicitly. */
  review_reason?: string | null;
  process_error?: string | null;
  operator_notes?: string | null;
}

/** Which half of the copy to resolve: the ≤3-word pill label, or the tooltip body. */
export type ReviewReasonPart = 'label' | 'detail';

const I18N_ROOT = 'orders.processing.review_reason';
const FALLBACK_LABEL_KEY = 'orders.processing.couldnt_process';
const FALLBACK_DETAIL_KEY = `${I18N_ROOT}.unknown_detail`;

/**
 * Resolves an ATO `needs_review` row to a **translation key** — always used with
 * the `translate` pipe: `row | reviewReason: 'label' | translate`.
 *
 * Pure by design: returning a key (rather than translated text) keeps the pipe
 * cheap and leaves language reactivity to `TranslatePipe`, which is already
 * impure and re-renders on `translate.use()`.
 *
 * Rows that predate the enum contract — or that were failed by the platform
 * itself rather than by an AI service (e.g. the stuck-row cron writes a full
 * sentence into `process_error`) — have no code to key off. Those fall back to
 * the generic pill label plus the raw backend text, which `translate` passes
 * through unchanged because it matches no key.
 */
@Pipe({
  name: 'reviewReason',
  standalone: true,
})
export class ReviewReasonPipe implements PipeTransform {
  transform(row: IReviewReasonSource | null | undefined, part: ReviewReasonPart): string {
    const code = this.resolveCode(row);
    if (code) {
      return `${I18N_ROOT}.${code}.${part}`;
    }

    if (part === 'label') {
      return FALLBACK_LABEL_KEY;
    }

    const raw = (row?.operator_notes || row?.process_error || '').trim();
    return raw || FALLBACK_DETAIL_KEY;
  }

  private resolveCode(row: IReviewReasonSource | null | undefined): ReviewReasonCode | null {
    const candidate = (row?.review_reason || row?.process_error || '').trim().toUpperCase();
    return (REVIEW_REASON_CODES as readonly string[]).includes(candidate)
      ? (candidate as ReviewReasonCode)
      : null;
  }
}
